/**
 * Scans server-state hooks (TanStack Query pilot).
 * Replaces the ScansPage history/schedule fetch + offline poll useEffects.
 */
import { useQuery } from '@tanstack/react-query';
import {
  getNetworkScanHistory,
  getNetworkScanSchedule,
  getRunningScans,
  getWebScanHistory,
  getWebScanSchedule,
} from '../../../services/scan.service';
import { getSites } from '../../../services/asset.service';
import {
  getEndpointAvailabilityStatus,
  getNetworkScanAvailabilityStatus,
  toUserFacingErrorMessage,
} from '../../../services/_core';
import {
  dedupeScanRowsByIdentity,
  getScansLifecycleKind,
  mapScheduledNetworkRowsToHistoryLikeRows,
  mapScheduledWebRowsToHistoryLikeRows,
  normalizeScansRow,
} from '../scans.utils';

function siteCatalogOrgName(site) {
  if (!site || typeof site !== 'object') return '';
  if (typeof site.organization === 'string') return site.organization.trim();
  return String(
    site.organization_name
      || site.org_name
      || site.organization?.name
      || site.organization?.org_name
      || ''
  ).trim();
}

function attachOrgSiteFromSitesCatalog(rows, sites) {
  if (!Array.isArray(rows) || !Array.isArray(sites) || sites.length === 0) return rows;
  const byUuid = new Map();
  sites.forEach((site) => {
    const uuid = String(site?.uu_id || site?.site_uuid || site?.uuid || '').trim();
    if (!uuid) return;
    byUuid.set(uuid, {
      orgName: siteCatalogOrgName(site),
      siteName: String(site?.site_name || site?.name || '').trim(),
    });
  });
  if (byUuid.size === 0) return rows;
  return rows.map((row) => {
    const siteUuid = String(row?.site_uuid || row?.site_uu_id || row?.site?.uu_id || '').trim();
    const match = siteUuid ? byUuid.get(siteUuid) : null;
    if (!match) return row;
    return {
      ...row,
      organization_name: row.organization_name || row.org_name || match.orgName,
      org_name: row.org_name || match.orgName,
      site_name: row.site_name || match.siteName,
    };
  });
}

function settledList(result) {
  if (result?.status === 'fulfilled' && Array.isArray(result.value)) return result.value;
  return [];
}

function settledRejected(result) {
  return result?.status === 'rejected';
}

function rejectionMessage(result, fallback) {
  if (!settledRejected(result)) return '';
  return toUserFacingErrorMessage(result.reason?.message || result.reason, fallback);
}

/**
 * Build list payload distinguishing true-empty from partial/total API failure.
 * Throws only when every required fetch failed (so React Query surfaces isError).
 */
function buildScanListResult({
  historyResult,
  scheduleResult,
  mapScheduled,
  emptyUnavailableInfo,
  bothFailedMessage,
}) {
  const historyFailed = settledRejected(historyResult);
  const scheduleFailed = settledRejected(scheduleResult);
  const historyRows = settledList(historyResult);
  const scheduledRows = settledList(scheduleResult);

  if (historyFailed && scheduleFailed) {
    const detail =
      rejectionMessage(historyResult, '') ||
      rejectionMessage(scheduleResult, '') ||
      bothFailedMessage;
    const err = new Error(toUserFacingErrorMessage(detail, bothFailedMessage));
    throw err;
  }

  const rows = dedupeScanRowsByIdentity([
    ...mapScheduled(scheduledRows),
    ...historyRows,
  ]);

  let info = '';
  if (rows.length === 0) {
    if (historyFailed || scheduleFailed) {
      const which = [
        historyFailed ? 'history' : null,
        scheduleFailed ? 'schedules' : null,
      ]
        .filter(Boolean)
        .join(' and ');
      info = `Unable to load scan ${which}. Showing available results only — try again shortly.`;
    } else if (emptyUnavailableInfo) {
      info = emptyUnavailableInfo;
    }
  } else if (historyFailed || scheduleFailed) {
    const which = [
      historyFailed ? 'history' : null,
      scheduleFailed ? 'schedules' : null,
    ]
      .filter(Boolean)
      .join(' and ');
    info = `Some scan ${which} could not be loaded. Showing partial results.`;
  }

  return { rows, info };
}

export function scansOrgQueryKey(orgQuery) {
  if (!orgQuery || typeof orgQuery !== 'object') return 'all';
  const orgId = String(orgQuery.org_id || orgQuery.orgId || '').trim();
  const siteId = String(orgQuery.site_uuid || orgQuery.site_id || orgQuery.siteId || '').trim();
  return `${orgId || 'all'}|${siteId || 'all'}`;
}

export async function fetchRunningScanList(orgQuery) {
  const rows = await getRunningScans(null, orgQuery);
  return Array.isArray(rows) ? rows : [];
}

export function mergeActiveScanRows({ runningRows = [], webRows = [], networkRows = [] } = {}) {
  const normalizedRunningRows = (Array.isArray(runningRows) ? runningRows : []).map((row) => {
    const backendType = String(row?.type || row?.scan_type || row?.scanType || '').toLowerCase();
    const scanType = backendType.includes('network') ? 'network' : 'web';
    return normalizeScansRow({ ...row, scanType });
  });
  const normalizedWebRows = (Array.isArray(webRows) ? webRows : [])
    .map((row) => normalizeScansRow({ ...row, scanType: 'web' }));
  const normalizedNetworkRows = (Array.isArray(networkRows) ? networkRows : [])
    .map((row) => normalizeScansRow({ ...row, scanType: 'network' }));

  return dedupeScanRowsByIdentity([
    ...normalizedRunningRows,
    ...normalizedWebRows,
    ...normalizedNetworkRows,
  ]).filter((row) => {
    const kind = getScansLifecycleKind(row.status, row.progressPercent, row.taskLifecycle);
    return kind === 'in_progress' || kind === 'recovering' || kind === 'scheduled';
  });
}

/** Narrow merged active rows to a single header status mode. */
export function filterActiveScansByStatus(rows, status = 'in_progress') {
  const mode = String(status || 'in_progress').trim().toLowerCase() === 'scheduled'
    ? 'scheduled'
    : 'in_progress';
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const kind = getScansLifecycleKind(row?.status, row?.progressPercent, row?.taskLifecycle);
    if (mode === 'scheduled') return kind === 'scheduled';
    return kind === 'in_progress' || kind === 'recovering';
  });
}

export async function fetchWebScanList(orgQuery) {
  const results = await Promise.allSettled([
    getWebScanHistory(null, orgQuery),
    getWebScanSchedule(null, orgQuery),
  ]);
  const availability = getEndpointAvailabilityStatus?.() || {};
  const emptyUnavailableInfo = availability.webScanHistoryUnavailable
    ? 'Web scan data is currently unavailable.'
    : '';

  let payload = buildScanListResult({
    historyResult: results[0],
    scheduleResult: results[1],
    mapScheduled: mapScheduledWebRowsToHistoryLikeRows,
    emptyUnavailableInfo,
    bothFailedMessage: 'Unable to load web scans. Please try again.',
  });

  // Schedule rows often include site_uuid/site_name but omit organization_name.
  // Backfill org (and blank site names) from the sites catalog, same as network.
  try {
    const sites = await getSites();
    payload = {
      ...payload,
      rows: attachOrgSiteFromSitesCatalog(payload.rows, sites),
    };
  } catch {
    /* catalog is optional — keep API row values */
  }

  return payload;
}

export async function fetchNetworkScanList(orgQuery) {
  const results = await Promise.allSettled([
    getNetworkScanHistory(null, orgQuery),
    getNetworkScanSchedule(null, orgQuery),
  ]);

  let payload = buildScanListResult({
    historyResult: results[0],
    scheduleResult: results[1],
    mapScheduled: mapScheduledNetworkRowsToHistoryLikeRows,
    emptyUnavailableInfo: '',
    bothFailedMessage: 'Unable to load network scans. Please try again.',
  });

  try {
    const sites = await getSites();
    payload = {
      ...payload,
      rows: attachOrgSiteFromSitesCatalog(payload.rows, sites),
    };
  } catch {
    /* catalog is optional — keep API row values */
  }

  if (payload.rows.length === 0 && !payload.info) {
    const availability = getNetworkScanAvailabilityStatus?.() || {};
    if (availability.anyHistoryUnavailable) {
      payload = { ...payload, info: 'Network scan data is currently unavailable.' };
    }
  }

  return payload;
}

export function useWebScansQuery({
  orgQuery = null,
  enabled = true,
  refreshKey = 0,
  refetchInterval = false,
} = {}) {
  return useQuery({
    queryKey: ['scans', 'web', scansOrgQueryKey(orgQuery), refreshKey],
    queryFn: () => fetchWebScanList(orgQuery),
    enabled,
    refetchInterval,
    placeholderData: (previousData) => previousData,
  });
}

export function useRunningScansQuery({
  orgQuery = null,
  enabled = true,
  refreshKey = 0,
  refetchInterval = 10000,
} = {}) {
  return useQuery({
    queryKey: ['scans', 'running', scansOrgQueryKey(orgQuery), refreshKey],
    queryFn: () => fetchRunningScanList(orgQuery),
    enabled,
    refetchInterval,
    placeholderData: (previousData) => previousData,
  });
}

export function useNetworkScansQuery({
  orgQuery = null,
  enabled = true,
  refreshKey = 0,
  refetchInterval = false,
} = {}) {
  return useQuery({
    queryKey: ['scans', 'network', scansOrgQueryKey(orgQuery), refreshKey],
    queryFn: () => fetchNetworkScanList(orgQuery),
    enabled,
    refetchInterval,
    placeholderData: (previousData) => previousData,
  });
}
